// Public marketing landing page — the signed-out front door at `/`. Pitches
// Bugpunch to prospective Unity studios (early-access positioning, no pricing)
// and routes every CTA to the contact / enquiry form (#contact). All claims
// are grounded in real SDK capabilities.

import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Bug, Terminal, Video, Gauge, MessageSquare,
  Smartphone, ShieldCheck, ChevronRight, CircleCheck, Sparkles,
} from '@/lib/icons';
import { MarketingHeader, MarketingFooter, MARKETING_FONT } from '@/components/marketing/chrome';
import { ContactForm } from '@/components/marketing/ContactForm';

const ORANGE = '#f97316';

const FEATURES: { icon: typeof Bug; title: string; body: string }[] = [
  { icon: Bug, title: 'Crash & error reporting',
    body: 'Native crashes, C# exceptions, and ANRs — captured even as the runtime dies, then auto-grouped by fingerprint with regression detection.' },
  { icon: Terminal, title: 'Live remote debugging',
    body: 'A browser Remote IDE: live hierarchy, inspector, console, and C# execution on your game running on real hardware.' },
  { icon: Video, title: 'Video & screenshot repro',
    body: 'Auto-captured screenshots and an opt-in rolling video buffer attach to every crash and report.' },
  { icon: Gauge, title: 'Performance & analytics',
    body: 'Native FPS, memory, and ANR monitoring, plus design, monetization, and progression events.' },
  { icon: MessageSquare, title: 'Player reports & feedback',
    body: 'Shake-to-report, in-game feedback, and live player chat — all on one Issues board.' },
];

const STEPS: { n: string; title: string; body: ReactNode }[] = [
  { n: '01', title: 'Add the package',
    body: <>One Git URL in your Unity manifest. No prefab, no <code className="font-mono text-white/80">Init()</code> — the SDK boots before your first scene.</> },
  { n: '02', title: 'Ship your build',
    body: <>Crash reporting, log capture, and the upload queue run automatically on Android, iOS, and Standalone.</> },
  { n: '03', title: 'Debug live',
    body: <>Open the dashboard to triage issues and attach to any device with the Remote IDE.</> },
];

const PLATFORMS: { icon: typeof Bug; name: string; body: string }[] = [
  { icon: Smartphone, name: 'Android', body: 'NDK signal handlers, ANR watchdog, MediaProjection video, logcat capture — all native.' },
  { icon: Smartphone, name: 'iOS', body: 'Mach exception + signal handlers, VideoToolbox ring buffer, OSLog capture, native upload queue.' },
  { icon: Terminal, name: 'Editor & Standalone', body: 'Managed exception capture and the full browser Remote IDE for Windows, macOS, and Linux.' },
];

export function LandingPage() {
  useEffect(() => { document.title = 'Bugpunch — Crash reporting & live debugging for Unity'; }, []);

  return (
    <div className="min-h-screen bg-[#0a0b0d] text-white" style={{ fontFamily: MARKETING_FONT }}>
      <MarketingHeader ctaHref="#contact" />

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* glow backdrop */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(60% 50% at 50% -10%, rgba(249,115,22,0.16), transparent 70%), radial-gradient(40% 40% at 85% 10%, rgba(0,122,204,0.14), transparent 70%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '56px 56px',
            maskImage: 'radial-gradient(70% 60% at 50% 0%, black, transparent 75%)',
          }}
        />
        <div className="relative mx-auto max-w-5xl px-5 pt-20 pb-16 text-center sm:px-8 sm:pt-28">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 rounded-full border border-[#f97316]/30 bg-[#f97316]/10 px-3 py-1 text-xs font-medium text-[#f9a25f]"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Now in early access
          </a>
          <h1 className="mx-auto mt-6 max-w-3xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight sm:text-6xl">
            Ship Unity games you can actually{' '}
            <span style={{ color: ORANGE }}>debug in production</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-white/55">
            An always-on crash reporter and live remote IDE for Unity. Capture native crashes, exceptions, ANRs,
            and video repro on every device — then debug the running game from your browser.
          </p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href="#contact"
              className="group inline-flex h-12 items-center gap-1.5 rounded-xl bg-[#f97316] px-6 text-[15px] font-semibold text-white shadow-[0_8px_30px_-6px_rgba(249,115,22,0.6)] transition-colors hover:bg-[#ea6a0c]"
            >
              Request access
              <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <Link
              to="/docs"
              className="inline-flex h-12 items-center gap-1.5 rounded-xl border border-white/15 bg-white/[0.03] px-6 text-[15px] font-medium text-white/85 transition-colors hover:bg-white/[0.07]"
            >
              Read the docs
            </Link>
          </div>
          <p className="mt-6 text-sm text-white/35">Android · iOS · Editor · Standalone — Unity 6000.0+</p>

          {/* install snippet */}
          <div className="mx-auto mt-12 max-w-2xl overflow-hidden rounded-xl border border-white/[0.08] bg-[#101216] text-left shadow-2xl">
            <div className="flex items-center gap-1.5 border-b border-white/[0.06] px-4 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="h-2.5 w-2.5 rounded-full bg-white/15" />
              <span className="ml-2 font-mono text-[11px] text-white/30">Packages/manifest.json</span>
            </div>
            <pre className="overflow-x-auto px-4 py-4 font-mono text-[12.5px] leading-relaxed text-white/75">
{`"dependencies": {
  "au.com.oddgames.bugpunch":
    "https://github.com/oddgames/bugpunch-sdk.git#v0.8.76"
}`}
            </pre>
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────── */}
      <Section id="features" eyebrow="Everything in one SDK" title="A complete debugging platform for Unity"
        blurb="One UPM package for crash reporting, live debugging, and player feedback — purpose-built for game runtimes.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6 transition-colors hover:border-white/[0.14] hover:bg-white/[0.04]">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#f97316]/12 text-[#f9a25f] ring-1 ring-inset ring-[#f97316]/20">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="text-[15px] font-semibold text-white">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── How it works ───────────────────────────────────────────── */}
      <Section id="how" eyebrow="Up and running in minutes" title="Three steps to production debugging"
        blurb="No SDK gymnastics. Add the package, ship, and start triaging real issues from real players.">
        <div className="grid gap-4 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} className="relative rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6">
              <span className="font-mono text-sm font-semibold text-[#f97316]">{s.n}</span>
              <h3 className="mt-3 text-[15px] font-semibold text-white">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Remote IDE highlight ───────────────────────────────────── */}
      <Section eyebrow="Live remote IDE" title="Inspect and control the running game — from your browser"
        blurb="When a debug device is connected, attach over a WebSocket tunnel and work as if the game were in your Editor.">
        <div className="grid gap-3 sm:grid-cols-2">
          {[
            'Browse the live GameObject hierarchy and edit component fields in real time',
            'Stream the Unity console with full log, warning, and error filtering',
            'Execute C# on the device with an IL2CPP-safe interpreter — no rebuild',
            'Watch a live WebRTC video stream and drive remote touch & click input',
          ].map((line) => (
            <div key={line} className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
              <span className="text-sm leading-relaxed text-white/70">{line}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Platforms ──────────────────────────────────────────────── */}
      <Section id="platforms" eyebrow="Every target, first-class" title="Native where it counts, managed where it helps"
        blurb="Bugpunch ships as prebuilt artifacts for each platform — no JDK, NDK, Xcode, or .NET SDK on your machine.">
        <div className="grid gap-4 md:grid-cols-3">
          {PLATFORMS.map((p) => (
            <div key={p.name} className="rounded-2xl border border-white/[0.07] bg-white/[0.02] p-6">
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-[#007acc]/12 text-[#4db8ff] ring-1 ring-inset ring-[#007acc]/25">
                <p.icon className="h-5 w-5" />
              </div>
              <h3 className="text-[15px] font-semibold text-white">{p.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/50">{p.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3 text-sm text-white/45">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          Works in Built-in, URP, and HDRP render pipelines.
        </div>
      </Section>

      {/* ── Contact / CTA ──────────────────────────────────────────── */}
      <section id="contact" className="relative scroll-mt-20 border-t border-white/[0.06] py-20">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(50% 60% at 50% 100%, rgba(249,115,22,0.10), transparent 70%)' }}
        />
        <div className="relative mx-auto grid max-w-5xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <h2 className="text-balance text-3xl font-semibold tracking-tight sm:text-4xl">
              Get early access to Bugpunch
            </h2>
            <p className="mt-4 max-w-md text-pretty text-base leading-relaxed text-white/55">
              Tell us about your game and we'll set you up with a project, an SDK key, and a walkthrough.
              We're onboarding studios one at a time.
            </p>
            <ul className="mt-6 space-y-2.5">
              {['Personalised onboarding for your team', 'Works with your existing Unity build pipeline', 'Direct line to the people building Bugpunch'].map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-sm text-white/65">
                  <CircleCheck className="h-4 w-4 shrink-0 text-[#f97316]" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-8">
            <ContactForm source="landing" />
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function Section({ id, eyebrow, title, blurb, children }: {
  id?: string; eyebrow: string; title: string; blurb: string; children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20 border-t border-white/[0.06] py-16 sm:py-20">
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="mx-auto mb-10 max-w-2xl text-center">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#f97316]">{eyebrow}</span>
          <h2 className="mt-2 text-balance text-3xl font-semibold tracking-tight sm:text-[34px]">{title}</h2>
          <p className="mx-auto mt-3 max-w-xl text-pretty text-[15px] leading-relaxed text-white/50">{blurb}</p>
        </div>
        {children}
      </div>
    </section>
  );
}
